package com.fireavert.administration.yardi_integration_page.yardi_data_import_section.client.presentation

import com.fireavert.administration.yardi_integration_page.presentation.ImportResults
import com.fireavert.administration.yardi_integration_page.presentation.ImportStatus
import com.fireavert.administration.yardi_integration_page.presentation.ImportSummaryItem
import com.fireavert.administration.yardi_integration_page.yardi_data_import_section.client.YardiPropertyImportInteractor
import com.fireavert.common.Try
import com.fireavert.properties.shared.client.ClientPropertyRepository
import com.fireavert.units.shared.client.UnitsRepository
import com.fireavert.yardi.common_data.client.YardiCommonDataClientRepository
import com.fireavert.yardi.common_data.data_models.requests.client_requests.YardiClientImportUnitData
import com.fireavert.yardi.common_data.data_models.requests.client_requests.YardiClientPropertyIdRequest
import com.fireavert.yardi.common_data.data_models.responses.UnitImportStatus
import kotlinx.datetime.Clock

class YardiPropertyImportInteractorImpl(
    // Inject dependencies here (repositories, services, etc.)
    private val yardiRepository: YardiCommonDataClientRepository,
    private val propertyRepository: ClientPropertyRepository,
    private val unitsRepository: UnitsRepository,
    private val viewModel: YardiPropertyImportViewModel
) : YardiPropertyImportInteractor {

    // Helper function to convert Yardi format (50201) to Dashboard format (502-1)
    private fun convertYardiToDashboardUnitId(yardiUnitId: String): String {
        if (yardiUnitId.length < 4) return yardiUnitId // Handle edge case

        val prefix = yardiUnitId.take(3)
        val suffix = yardiUnitId.drop(3).toIntOrNull()?.toString() ?: yardiUnitId.drop(3)

        return "$prefix-$suffix"
    }

    // Function to create mapping from Yardi unit IDs to Dashboard unit IDs
    private fun createUnitMapping(yardiUnitNames: List<String>, dashboardUnitNames: List<String>): Map<String, String> {
        val dashboardUnitSet = dashboardUnitNames.toSet()

        return yardiUnitNames.mapNotNull { yardiUnit ->
            val expectedDashboardUnit = convertYardiToDashboardUnitId(yardiUnit)
            if (expectedDashboardUnit in dashboardUnitSet) {
                yardiUnit to expectedDashboardUnit
            } else null
        }.toMap()
    }

    // Function to create index-based mapping for array access
    private fun createUnitIndexMapping(yardiUnitNames: List<String>, dashboardUnitNames: List<String>): Map<String, Int> {
        val dashboardUnitMap = dashboardUnitNames.withIndex().associate { (index, unit) -> unit to index }

        return yardiUnitNames.mapNotNull { yardiUnit ->
            val expectedDashboardUnit = convertYardiToDashboardUnitId(yardiUnit)
            val dashboardIndex = dashboardUnitMap[expectedDashboardUnit]
            if (dashboardIndex != null) {
                yardiUnit to dashboardIndex
            } else null
        }.toMap()
    }

    override suspend fun onLoad() {
        try {
            viewModel.loading = true
            viewModel.clearError()

            viewModel.propertyList = when (val maybe = propertyRepository.getPropertiesNameAndIdMap()) {
                is Try.Success -> maybe.value
                is Try.Error -> emptyMap() // Handle error appropriately
            }

        } catch (e: Exception) {
            viewModel.error = "Failed to load initial data: ${e.message}"
        } finally {
            viewModel.loading = false
        }
    }

    override suspend fun refreshYardiProperties(credentialsId: Int, yardiPropertyId: String) {
        try {
            viewModel.loading = true
            viewModel.clearError()

            // Call Yardi API to get properties for the credentials
            // val properties = yardiRepository.getProperties(credentialsId)

            val yardiPropertyResult = when (val maybe = yardiRepository.yardiGetPropertyList(
                YardiClientPropertyIdRequest(
                    credentialsId, yardiPropertyId
                )
            )) {
                is Try.Success -> maybe.value
                is Try.Error -> {
                    viewModel.error = "Failed to fetch Yardi properties: ${maybe.exception.message}"
                    return
                }
            }
            // Mock data for now
            viewModel.yardiPropertyList = yardiPropertyResult.map { it.marketingName }.sorted()

        } catch (e: Exception) {
            viewModel.error = "Failed to refresh Yardi properties: ${e.message}"
        } finally {
            viewModel.loading = false
        }
    }

    override suspend fun loadYardiUnitNames(credentialsId: Int, propertyId: String) {
        try {
            viewModel.loading = true
            viewModel.clearError()

            // Call Yardi API to get unit names for the property
            // val unitNames = yardiRepository.getUnitNames(credentialsId, propertyId)
            val units = when (val maybe = yardiRepository.yardiGetUnitData(
                YardiClientPropertyIdRequest(
                    credentialsId,
                    propertyId
                )
            )) {
                is Try.Success -> maybe.value
                is Try.Error -> {
                    viewModel.error = "Failed to fetch Yardi unit names: ${maybe.exception.message}"
                    return
                }
            }

            viewModel.yardiUnitNames = units.units.map { it.unitId }.sorted()


        } catch (e: Exception) {
            viewModel.error = "Failed to load Yardi unit names: ${e.message}"
        } finally {
            viewModel.loading = false
        }
    }

    override suspend fun loadDashboardUnitNames(propertyId: Int) {
        try {
            viewModel.loading = true
            viewModel.clearError()

            viewModel.dashboardUnitNames = when (val maybe = unitsRepository.getUnitsForPropertyId(propertyId)) {
                is Try.Success -> maybe.value.map { it.number }.sorted()
                is Try.Error -> {
                    viewModel.error = "Failed to fetch dashboard unit names: ${maybe.exception.message}"
                    emptyList()
                }
            }

        } catch (e: Exception) {
            viewModel.error = "Failed to load dashboard unit names: ${e.message}"
        } finally {
            viewModel.loading = false
        }
    }

    override suspend fun startDataImport(
        propertyId: String,
        credentialsId: Int,
        yardiPropertyId: String,
        parsingMethod: String,
        yardiUnitNames: List<String>,
        dashboardUnitNames: List<String>
    ) {
        try {
            print("starting import")
            viewModel.importInProgress = true
            viewModel.clearError()

            // Generate import ID
            val importId = "import_${Clock.System.now().epochSeconds}"
            viewModel.importId = importId

            // Start the import process
            // This would typically be a long-running operation
            // You might want to use a background job or coroutine for this
            var parsingMap = emptyMap<String, Int>()
            var unitMapping = emptyMap<String, String>()
            if (parsingMethod == "Solomon") {
                // Create the unit mapping
                unitMapping = createUnitMapping(yardiUnitNames, dashboardUnitNames)
                parsingMap = createUnitIndexMapping(yardiUnitNames, dashboardUnitNames)

                println("Created parsing map with ${parsingMap.size} mappings")

                // Check for unmapped units
                val unmappedYardiUnits = yardiUnitNames.filter { it !in unitMapping.keys }
                val unmappedDashboardUnits = dashboardUnitNames.filter { dashboardUnit ->
                    !unitMapping.values.contains(dashboardUnit)
                }

                if (unmappedYardiUnits.isNotEmpty()) {
                    println("Warning: ${unmappedYardiUnits.size} Yardi units could not be mapped: ${unmappedYardiUnits.take(5)}")
                }
                if (unmappedDashboardUnits.isNotEmpty()) {
                    println("Warning: ${unmappedDashboardUnits.size} Dashboard units have no Yardi match: ${unmappedDashboardUnits.take(5)}")
                }
            }

            viewModel.updateImportProgress(0.1f, "Starting import...")

            val response = when (val maybe = yardiRepository.importYardiUnitData(
                YardiClientImportUnitData(
                    propertyId = propertyId.toInt(),
                    yardiCredentialId = credentialsId,
                    yardiPropertyId = yardiPropertyId,
                    yardiToConnectUnitMap = unitMapping
                )
            )) {
                is Try.Success -> maybe.value
                is Try.Error -> {
                    viewModel.error = "Failed to start import: ${maybe.exception.message}"
                    viewModel.updateImportProgress(0f, "Import failed")
                    return
                }
            }

            // Store the full response
            viewModel.yardiImportResponse = response

            // Convert unit results to import summary items
            val importSummaryItems = response.unitResults.map { unitResult ->
                ImportSummaryItem(
                    unitName = unitResult.yardiUnitNumber ?: unitResult.yardiUnitId,
                    status = when (unitResult.status) {
                        UnitImportStatus.SUCCESS -> ImportStatus.SUCCESS
                        UnitImportStatus.FAILED_DATABASE_ERROR,
                        UnitImportStatus.FAILED_TENANT_CREATION,
                        UnitImportStatus.FAILED_OTHER -> ImportStatus.FAILED

                        UnitImportStatus.SKIPPED_NO_MAPPING,
                        UnitImportStatus.SKIPPED_NO_CONNECT_UNIT,
                        UnitImportStatus.SKIPPED_NO_CURRENT_RESIDENT -> ImportStatus.SKIPPED
                    },
                    message = unitResult.reason ?: unitResult.errorMessage ?: when (unitResult.status) {
                        UnitImportStatus.SUCCESS -> "Successfully imported tenant: ${unitResult.tenantInfo?.firstName} ${unitResult.tenantInfo?.lastName}"
                        else -> "Import failed"
                    },
                    yardiUnitId = unitResult.yardiUnitId,
                    dashboardUnitId = unitResult.connectUnitNumber
                )
            }

            // Set final results
            viewModel.importResults = ImportResults(
                totalUnitsProcessed = response.totalUnitsAttempted,
                successfulImports = response.unitsSuccessfullyImported,
                failedImports = response.unitsFailedToImport,
                skippedUnits = response.unitsSkipped,
                importSummary = importSummaryItems,
                errors = response.generalErrors,
                warnings = response.unitResults
                    .filter {
                        it.status == UnitImportStatus.SKIPPED_NO_MAPPING ||
                                it.status == UnitImportStatus.SKIPPED_NO_CONNECT_UNIT ||
                                it.status == UnitImportStatus.SKIPPED_NO_CURRENT_RESIDENT
                    }
                    .map { "${it.yardiUnitNumber ?: it.yardiUnitId}: ${it.reason}" },
                completedAt = Clock.System.now().toString(),
            )

            viewModel.updateImportProgress(1.0f,
                if (response.success) "Import completed successfully!"
                else "Import completed with errors"
            )

        } catch (e: Exception) {
            viewModel.error = "Import failed: ${e.message}"
        } finally {
            viewModel.importInProgress = false
        }
    }

    override suspend fun getImportResults(importId: String) {
        try {
            viewModel.loading = true
            viewModel.clearError()

            // Get import results from database/service
            // val results = importService.getResults(importId)
            // viewModel.importResults = results

        } catch (e: Exception) {
            viewModel.error = "Failed to get import results: ${e.message}"
        } finally {
            viewModel.loading = false
        }
    }

    override suspend fun cancelImport(importId: String) {
        try {
            viewModel.loading = true
            viewModel.clearError()

            // Cancel the import process
            // importService.cancel(importId)

            viewModel.importInProgress = false
            viewModel.importStatus = "Import cancelled"

        } catch (e: Exception) {
            viewModel.error = "Failed to cancel import: ${e.message}"
        } finally {
            viewModel.loading = false
        }
    }
}